import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Step,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const ShiftsAndDocuments = () => (
  <Page
    title="Shifts and Documents"
    intro="Shift cover and event files are managed from the event record: the Shifts panel groups every duty by day, the Invoicing panel in the right-hand column holds quotes and invoices, and the Documents and Participants panels lower down hold uploaded files and the organiser's entry list."
  >
    <Section title="Reading the Shifts panel">
      <Bullets>
        <Bullet>
          The panel header shows an overall <strong>filled / total</strong>{' '}
          count. Shifts are then grouped into a per-day accordion headed with
          the date and that day&apos;s own count — the green or red edge and
          chip show at a glance whether the day is fully covered.
        </Bullet>
        <Bullet>
          Each row shows the time window, hours, clinical role, the assigned
          person (avatar and name, or an <strong>Unassigned</strong> chip),
          the event role, and a green tick when the shift is bookable by
          staff.
        </Bullet>
        <Bullet>
          A note icon shows the shift note on hover, and a purple person icon
          means the shift has <strong>Pending Applications</strong> — clicking
          it takes you to the Shift Applications screen.
        </Bullet>
        <Bullet>
          <strong>Add shift</strong> sits in the header. The three-dot menu
          beside it holds everything else: <strong>Mass update</strong>,{' '}
          <strong>Make all shifts
          bookable</strong> / <strong>unbookable</strong>, the{' '}
          <strong>SMS</strong> and <strong>Email</strong> broadcasts to
          assigned staff, <strong>Notify eligible staff</strong>, and{' '}
          <strong>Rejected shift applications</strong>. Notify eligible staff
          contacts everyone whose enabled roles match a vacant bookable shift
          (by email and/or app push, following each person&apos;s own
          notification preferences). A site administrator can remove that
          option, and switch off the automatic daily and weekly digests, under{' '}
          <strong>Configuration &gt; Shifts &gt; Available Shift
          Notifications</strong>.
        </Bullet>
        <Bullet>
          <strong>Mass update</strong> changes many shifts at once. On the
          left, narrow the list by day, clinical role, event role, shift times
          or status (assigned, unassigned, bookable) and tick the shifts to
          change. On the right, switch on only the changes you want — set new
          start/end dates and times (any field left blank keeps each
          shift&apos;s own) or move shifts earlier or later, change the role,
          pay rate, event role or bookable setting, add to or replace the
          notes, or add/remove a fixed bonus. Each shift shows its old and new
          times before you save. Paid, verified and completed shifts are
          locked. If an assigned person can&apos;t work a changed shift (for
          example they aren&apos;t enabled for the new role) nothing is saved
          and that shift is highlighted. Staff on moved shifts are told their
          new times, and pending applications on moved open shifts lapse.
        </Bullet>
        <Bullet>
          Sending an SMS from this menu records an{' '}
          <strong>Internal Note</strong> on the event with the recipients and
          the message, so the broadcast stays on the event&apos;s record. The
          full log is still available under Configuration&apos;s SMS History.
        </Bullet>
        <Bullet>
          The <strong>Rejected shift applications</strong> menu option lists
          staff whose applications were turned down but who may still be free.
          Staff who can&apos;t work a period record it from My Availability,
          and the assignment checks pick that up automatically.
        </Bullet>
      </Bullets>
      <Info title="Who can do this?">
        Managing event shifts needs the <strong>Event Planning</strong> team.
        The <strong>Notify eligible staff of available shifts</strong> button is the
        exception — it needs the <strong>Personnel</strong> team, so it fails
        for planners without it. Reviewing applications needs Personnel or
        Event Planning, and verifying completed shifts (from Payroll) needs
        Personnel.
      </Info>
    </Section>

    <Section title="Adding a shift">
      <Step number="1" title="Open the dialog">
        Click <strong>Add shift</strong> (or click an existing row to edit
        it).
      </Step>
      <Step number="2" title="Set times, role, and pay">
        Choose the <strong>Start</strong> and <strong>End</strong>, a{' '}
        <strong>Role</strong> (which auto-fills the default hourly rate for
        that role), and optionally an <strong>Event Role</strong> such as
        Ambulance, RRV, Medical Centre, or Foot Patrol. The{' '}
        <strong>Rate</strong> can be hourly or fixed per shift, and{' '}
        <strong>Shift Notes</strong> are visible to staff. Fixed{' '}
        <strong>bonuses</strong> (a label and an amount, e.g. "Vehicle pickup
        £20") can be added on top of the rate — they follow the shift through
        payroll, My Pay, and the event's staff cost. Use{' '}
        <strong>Add paid travel time</strong> when travel to and from the
        event is paid at a different rate: enter the minutes before and after
        the shift and the travel rate per hour. The shift times stay as the
        on-site window; travel is paid on top.
      </Step>
      <Step number="3" title="Decide how it gets filled">
        Leave <strong>Staff can book</strong> on to advertise it on Available
        Shifts, or pick someone directly in{' '}
        <strong>Assigned staff</strong> — the selector searches staff and
        approved subcontractors, and there's a checkbox for cover by a
        subcontractor without a user account.
      </Step>
      <Step number="4" title="Use Number of shifts for multiple people">
        <strong>Number of shifts</strong> creates that many identical copies
        of the shift — same times, role, and pay. When creating several, an
        assignment row appears for each slot, so you can assign each one to a
        different person (or a subcontractor) or leave it open. On the event
        they appear as separate rows, but staff see open slots as a single
        merged listing — see the Shift Applications and Acknowledgements page
        for how applications are shared out.
      </Step>
      <Step number="5" title="Repeat the shift across several days">
        Turning on <strong>Repeat</strong> (add mode only) saves the same
        shift on every matching day, each as its own shift. Choose the days of
        the week to repeat on — with <strong>Every day</strong>,{' '}
        <strong>Weekdays</strong>, and <strong>Weekends</strong> presets —
        and an interval of every 1 to 4 weeks. Repeat{' '}
        <strong>until the event ends</strong> (the default for multi-day
        events), for 1, 2, 4, or 8 weeks, or pick your own{' '}
        <strong>Repeat until</strong> date. You can assign the same staff
        every day, or switch that off and assign each day individually —
        slots left empty are advertised as open vacancies. A repeat is capped
        at 100 days and 400 shifts in one go.
      </Step>
      <Screenshot url="events/add-shift-repeat.png">
        The Add Shift dialog with Repeat turned on, showing the day-of-week
        presets and the repeat-until options.
      </Screenshot>
      <Info title="Assignment checks">
        Direct assignments are checked before saving: staff who aren't live,
        don't have the role enabled, have a clashing shift or approved leave
        or unavailability, have expired mandatory training, or lack a valid
        DBS are blocked, with the reasons listed. Non-blocking issues
        (like a pending application elsewhere) show as warnings.
      </Info>
    </Section>

    <Section title="Changing and removing assignments">
      <Bullets>
        <Bullet>
          Clearing or changing the assigned person opens a confirmation that
          requires a <strong>Reason for Cancellation</strong> (Event
          Cancelled, Staff no longer available, Shift Swapped, Sickness, or
          your own wording). The staff member is emailed automatically, and
          any application they had for the shift is removed.
        </Bullet>
        <Bullet>
          The dialog warns you if removing them will also delete an overtime
          claim or clock record. Removal is refused entirely once the shift is
          paid or in a pay run.
        </Bullet>
        <Bullet>
          Shifts become <strong>locked</strong> once completed by staff,
          verified, or paid — edit and delete are disabled. Unassign staff
          before deleting a shift, and review pending applications before
          reassigning one that has them.
        </Bullet>
        <Bullet>
          <strong>Duplicate Shift</strong> in the dialog's menu copies an
          unassigned shift as many times as you need.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Invoicing">
      <Bullets>
        <Bullet>
          The <strong>Invoicing</strong> panel sits in the right-hand column
          below the Event Medical Plan card. Three tiles summarise the latest{' '}
          <strong>quoted</strong> value, the total <strong>invoiced</strong>,
          and what is still <strong>outstanding</strong>.
        </Bullet>
        <Bullet>
          Use the plus menu for <strong>Create quote</strong>,{' '}
          <strong>Create invoice</strong>, and{' '}
          <strong>Invoice from accepted quote</strong>, which pre-fills the
          invoice lines from the accepted quote — it stays disabled until a
          quote has been accepted.
        </Bullet>
        <Bullet>
          Quotes are listed with their value, date, author, and status; the
          eye and pencil icons view or edit one. Invoices show the reference,
          due date, and total, with a status chip and the balance still due —
          click a row to open it in the invoice builder.
        </Bullet>
        <Bullet>
          The footer shows the <strong>estimated staff wages</strong> for the
          event, calculated from the shifts' rates, bonuses, and paid travel —
          useful for sanity checking margins. It is informational only:
          invoice lines come from the quote, not from wages.
        </Bullet>
        <Bullet>
          See the Finance documentation section for the invoice builder,
          payments, and credit notes.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Documents">
      <Bullets>
        <Bullet>
          The <strong>Documents</strong> panel sits in the row below Tasks and
          Internal Notes, alongside Participants and Client Feedback. Each
          file shows its name, who
          uploaded it and when, and a chip marking it as visible to{' '}
          <strong>All staff</strong> or <strong>Planners</strong> only. Click
          a row to preview or download it.
        </Bullet>
        <Bullet>
          Use the upload icon to add a file. The{' '}
          <strong>Viewable by all staff</strong> switch controls whether the
          document appears on the read-only event view. Images, PDFs, Word
          documents, and GPX files are accepted.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Participant lists">
      <p>
        The <strong>Participants</strong> panel (beside Documents) holds the
        event organiser&apos;s entry list — runners, riders, or other
        participants with their bib or race numbers. Once a list is uploaded,
        crews writing an ePCR linked to this event can search it by bib number
        and pre-fill the patient&apos;s details after confirming the match —
        see{' '}
        <DocLink
          docsPage
          url="/clinical-records/starting-and-finding"
          text="Starting and Finding ePCRs"
        />
        .
      </p>
      <Bullets>
        <Bullet>
          Upload a <strong>CSV or TSV</strong> file. Column headings are
          matched flexibly — <strong>Race Bib number</strong>,{' '}
          <strong>Bib</strong>, <strong>Race No</strong>, and{' '}
          <strong>Runner Number</strong> all work for the number column, and
          the same applies to names, date of birth, address, mobile, club, and
          emergency contact columns. Unrecognised columns are simply ignored.
          A <strong>Download Template</strong> button in the upload dialog
          shows the full set, including optional{' '}
          <strong>Medical Notes</strong>, <strong>Allergies</strong>, and{' '}
          <strong>Medications</strong> columns.
        </Bullet>
        <Bullet>
          Before anything is saved you see a preview with the parsed count and
          any warnings: rows without a bib number are skipped, duplicate bib
          numbers keep the first row, and an unreadable date of birth imports
          the row without it.
        </Bullet>
        <Bullet>
          Each upload <strong>replaces</strong> the stored list in full — to
          take an updated entry list from the organiser, just upload the new
          file. The panel shows the file name, participant count, and upload
          date, and the bin icon removes the list entirely. ePCRs that already
          copied a participant&apos;s details keep them either way.
        </Bullet>
      </Bullets>
      <Info title="Who can do this?">
        Uploading and removing participant lists needs the{' '}
        <strong>Event Planning</strong> team. Searching the list from an ePCR
        is open to any signed-in clinician. Lists are capped at 20,000
        participants per event.
      </Info>
      <Warning title="Personal data">
        Entry lists contain personal data. Only upload lists the organiser has
        provided for medical cover purposes, and remove the list when it is no
        longer needed.
      </Warning>
    </Section>
  </Page>
)

export default ShiftsAndDocuments
